import { useEffect, useRef, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { useRouter } from "@tanstack/react-router";

/**
 * Client-side link that accepts a plain href, including query strings and ids.
 *
 * Uses the router's `navigate` / `preloadRoute` pipeline (not a bare history push)
 * so code-split route chunks can warm on hover and pending UI can paint while they
 * load — without that, a sidebar click sat on the old screen for seconds with no
 * feedback.
 */
export function HrefLink({
  to,
  children,
  className,
  onClick,
  replace,
  ...rest
}: {
  to: string;
  children?: ReactNode;
  className?: string;
  replace?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
  const router = useRouter();
  const preloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (preloadTimer.current) clearTimeout(preloadTimer.current);
    };
  }, []);

  const clearPreloadTimer = () => {
    if (preloadTimer.current) {
      clearTimeout(preloadTimer.current);
      preloadTimer.current = null;
    }
  };

  const preload = () => {
    clearPreloadTimer();
    if (router.options.defaultPreload === false) return;
    /* `href` is enough at runtime; the NavigateOptions generic still wants `to`. */
    void router.preloadRoute({ href: to } as Parameters<typeof router.preloadRoute>[0]).catch(() => {
      /* Preload is best-effort; a failed warm-up must not surface as a navigation error. */
    });
  };

  const enqueuePreload = () => {
    if (router.options.defaultPreload === false) return;
    clearPreloadTimer();
    const delay = router.options.defaultPreloadDelay ?? 50;
    preloadTimer.current = setTimeout(preload, delay);
  };

  return (
    <a
      href={to}
      className={className}
      onMouseEnter={enqueuePreload}
      onFocus={enqueuePreload}
      onMouseLeave={clearPreloadTimer}
      onBlur={clearPreloadTimer}
      onTouchStart={preload}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
          return;
        }
        event.preventDefault();
        clearPreloadTimer();
        void router.navigate({ href: to, replace } as Parameters<typeof router.navigate>[0]);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
